import { useEffect, useRef } from 'react';
import { pingVisit, sendActiveBeat } from '../../services/visitTrackingService';

const HEARTBEAT_INTERVAL_MS = 60_000; // identity ping — unchanged from before
const TICK_MS = 5_000; // how often we sample elapsed time into the accumulator
const BEAT_INTERVAL_MS = 30_000; // how often we flush the accumulator to the server
const MAX_TICK_CREDIT_MS = TICK_MS * 3; // guards against crediting a laptop-sleep/suspend gap as active time

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  // Use cryptographically secure random bytes when available.
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    const randomHex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `session-${Date.now()}-${randomHex}`;
  }
  // Last-resort fallback if Web Crypto is unavailable.
  return `session-${Date.now()}-fallback`;
}

/** Tab visible AND window focused — no idle timeout on top of this, by design. */
function isPageActive(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

/**
 * Renders nothing. Tracks one visit session for as long as this component
 * stays mounted (i.e. for as long as someone has the Automation Insights
 * page open):
 *
 *   - An identity ping fires once on mount, then every 60s — unchanged from
 *     the original 2026-08-25 version. This only ever touches "who was seen
 *     and when," never duration.
 *
 *   - A real active-time tracker samples elapsed time every 5s and adds it
 *     to an in-memory accumulator ONLY when the tab is visible AND the
 *     window is focused (isPageActive()) — no idle timeout beyond that, per
 *     product decision. Every 30s, and immediately whenever the page becomes
 *     hidden, the accumulator is flushed to the backend via sendActiveBeat()
 *     and reset to 0. This is what fixes the old bug where leaving a tab
 *     open in the background kept inflating "time spent."
 *
 *   - Each 5s tick credits at most 15s (MAX_TICK_CREDIT_MS) even if more
 *     real time elapsed, so a laptop sleep/resume or a long-suspended
 *     background tab can never be credited as active time just because the
 *     browser eventually fires another interval tick.
 *
 * Rewritten 2026-09-11 (was a flat 60s heartbeat with zero visibility
 * awareness). Deliberately still minimal and self-contained:
 *   - All timers are created once (mount) and cleared on unmount — bounded,
 *     no drift, no leaked timers across navigation.
 *   - Every network call (pingVisit / sendActiveBeat) swallows its own
 *     errors, so nothing here can ever throw or affect the rest of the page.
 *   - Safe to delete this one <VisitTracker /> line anywhere it's mounted to
 *     disable the entire frontend side of this feature instantly.
 */
export default function VisitTracker() {
  const sessionIdRef = useRef<string>(generateSessionId());

  useEffect(() => {
    const sessionId = sessionIdRef.current;

    pingVisit(sessionId);
    const heartbeat = setInterval(() => {
      pingVisit(sessionId);
    }, HEARTBEAT_INTERVAL_MS);

    let accumulatedMs = 0;
    let lastTickAt = Date.now();
    let wasActive = isPageActive();

    const sample = () => {
      const now = Date.now();
      const elapsed = now - lastTickAt;
      lastTickAt = now;
      const wasActiveBeforeSample = wasActive;
      wasActive = isPageActive();
      if (wasActiveBeforeSample) {
        accumulatedMs += Math.min(elapsed, MAX_TICK_CREDIT_MS);
      }
    };

    const flush = (keepalive: boolean) => {
      const seconds = Math.floor(accumulatedMs / 1000);
      if (seconds > 0) {
        accumulatedMs -= seconds * 1000;
        sendActiveBeat(sessionId, seconds, { keepalive });
      }
    };

    const tick = setInterval(sample, TICK_MS);
    const beat = setInterval(() => {
      sample();
      flush(false);
    }, BEAT_INTERVAL_MS);

    // Flush (and stop crediting) the moment the page is hidden — covers tab
    // switches, minimizing, and most real navigations/closes. keepalive so
    // the request has a chance to land even if the page is being torn down.
    const handleVisibilityChange = () => {
      sample();
      if (document.visibilityState === 'hidden') {
        flush(true);
        lastTickAt = Date.now();
      }
    };

    // Recompute immediately on focus/blur too, so a blur mid-tick doesn't
    // wait up to TICK_MS to stop crediting active time, and a refocus starts
    // counting fresh from that instant.
    const handleFocusChange = () => {
      sample();
    };

    // Last-resort net in case visibilitychange doesn't fire before the page
    // is actually torn down (varies by browser).
    const handlePageHide = () => {
      sample();
      flush(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocusChange);
    window.addEventListener('blur', handleFocusChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      clearInterval(heartbeat);
      clearInterval(tick);
      clearInterval(beat);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocusChange);
      window.removeEventListener('blur', handleFocusChange);
      window.removeEventListener('pagehide', handlePageHide);
      sample();
      flush(false);
    };
  }, []);

  return null;
}
